import { lazy, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AuthScreen, RecoveryKeyDialog } from './core/auth/AuthScreens';
import { AuthService, type Session } from './core/auth/AuthService';
import type { HomeState } from './home/data/types';
import { HomePage } from './home/HomePage';
import { AppShell } from './shell/AppShell';
import { isDemoSearch } from './shell/links';
import { userFromEmail, type ShellUser } from './shell/ShellContext';
import './modules/vault/register';

// Le code de chaque module est chargé à la demande (CLAUDE.md §13).
const NotesRoot = lazy(() => import('./modules/notes/NotesRoot'));
const VaultRoot = lazy(() => import('./modules/vault/VaultRoot'));

const DEMO_USER: ShellUser = { name: 'Alex', initial: 'A' };

/** Accueil. En démonstration, `?homeState=empty|loading` force l'état affiché (revue visuelle) ; jamais en usage réel. */
function HomeRoute() {
  const { search } = useLocation();
  const forced = isDemoSearch(search) ? new URLSearchParams(search).get('homeState') : null;
  const state: HomeState = forced === 'empty' || forced === 'loading' ? forced : 'normal';
  return <HomePage state={state} />;
}

/** Écran Notes. L'intention « nouvelle note » du raccourci d'Accueil est lue une fois puis effacée de l'historique. */
function NotesRoute({ session, onLogout }: { session: Session | null; onLogout?: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [intent] = useState<'new-note' | undefined>(() => ((location.state as { intent?: string } | null)?.intent === 'new-note' ? 'new-note' : undefined));

  useEffect(() => {
    if (location.state) navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
  }, [location.state, location.pathname, location.search, navigate]);

  return <NotesRoot session={session} onLogout={onLogout} intent={intent} />;
}

/** Écran Mots de passe. L'intention « nouveau mot de passe » du raccourci d'Accueil est lue une fois puis effacée de l'historique. */
function VaultRoute({ session, onLogout }: { session: Session | null; onLogout?: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [intent] = useState<'new-password' | undefined>(() => ((location.state as { intent?: string } | null)?.intent === 'new-password' ? 'new-password' : undefined));

  useEffect(() => {
    if (location.state) navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
  }, [location.state, location.pathname, location.search, navigate]);

  return <VaultRoot session={session} onLogout={onLogout} intent={intent} />;
}

function ToHome() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: '/', search }} replace />;
}

export function App() {
  const [auth] = useState(() => new AuthService());
  const [demo] = useState(() => typeof window !== 'undefined' && isDemoSearch(window.location.search));
  const state = useSyncExternalStore(auth.subscribe, auth.getState);
  const session = auth.getSession();
  const onLogout = useCallback(() => void auth.logout(), [auth]);
  const user = useMemo<ShellUser | null>(() => (demo ? DEMO_USER : session ? userFromEmail(session.email) : null), [demo, session]);

  // Sans compte, rien n'est affiché de la coque ; la démonstration (`?demo`) n'a ni compte ni serveur.
  if (!demo && (state.status !== 'signed-in' || !session || !user)) return <AuthScreen auth={auth} />;
  if (!user) return null;
  const shellSession = demo ? null : session;
  const shellLogout = demo ? undefined : onLogout;

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AppShell session={shellSession} user={user} onLogout={shellLogout} />}>
          <Route index element={<HomeRoute />} />
          <Route path="notes/*" element={<NotesRoute session={shellSession} onLogout={shellLogout} />} />
          <Route path="vault/*" element={<VaultRoute session={shellSession} onLogout={shellLogout} />} />
          <Route path="*" element={<ToHome />} />
        </Route>
      </Routes>
      {!demo && state.recoveryKey ? <RecoveryKeyDialog recoveryKey={state.recoveryKey} onDone={() => auth.acknowledgeRecoveryKey()} /> : null}
    </BrowserRouter>
  );
}
